import { camelCase } from 'es-toolkit';
import { produce } from 'immer';

import { Insertion, LayoutModel, Project } from '@paragon/contract-library';

import { getCategoryForLayoutModel, isComponentDesignInsertion } from './classifiers';

export interface LoadedProjectData {
  readonly models: readonly LayoutModel[];
  readonly insertions: readonly Insertion[];
  readonly componentDesigns: ReadonlySet<string>;
}

export function createEmptyLoadedData(): LoadedProjectData {
  return { models: [], insertions: [], componentDesigns: new Set() };
}

/**
 * Migrates incoming layout models from the server to account for client-side differences in the schema.
 */
export function migrateIncomingLayoutModel(model: any): LayoutModel {
  return {
    ...model,
    type: camelCase(model.modelType), // the layout models library expects a type property on every model
    category: getCategoryForLayoutModel(model), // the category property does not exist on server-side models
  };
}

/**
 * This function should always be called on projects loaded from the server before they are used client-side.
 */
export function migrateIncomingProject(project: Project): LoadedProjectData {
  const sourceModels = Object.values(project.layoutModels) as any[];
  sourceModels.push(
    produce<any>(project, (draft) => {
      draft.modelType = 'Project';
      delete draft.layoutModels;
      delete draft.insertions;
    }),
  );

  const models = sourceModels.map(migrateIncomingLayoutModel);
  const { insertions } = project;
  const componentDesigns = new Set<string>();

  for (const insertion of insertions) {
    if (isComponentDesignInsertion(insertion)) {
      componentDesigns.add(insertion.objectGuid);
    }
  }

  return { models, insertions, componentDesigns };
}

export function combineLoadedProjectData(a: LoadedProjectData, b: LoadedProjectData): LoadedProjectData {
  return {
    models: a.models.concat(b.models),
    insertions: a.insertions.concat(b.insertions),
    componentDesigns: new Set([...a.componentDesigns, ...b.componentDesigns]),
  };
}
